import { i3Assets } from '../utils/i3Assets';
import type {
  I3BlogPostCms,
  I3EventCarouselSlide,
  I3EventsPageCms,
  I3FeaturedEvent,
  I3HomeEventSpotlight,
  I3LatestEventHighlight,
  I3OperationsSnapshot,
  I3PastEvent,
  I3PastEventMedia,
  I3SaveTheDateAnnouncement,
  I3ServiceCard,
  I3ServicesCms,
  I3ServicesPillar,
  I3SiteContentV1,
  I3UpcomingEvent,
} from './types';
import { DEFAULT_SITE_CONTENT } from './defaultSiteContent';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function parseMedia(raw: unknown): I3PastEventMedia[] {
  if (!Array.isArray(raw)) return [];
  const out: I3PastEventMedia[] = [];
  for (const item of raw) {
    if (!isRecord(item)) continue;
    const type = item.type === 'video' || item.type === 'image' ? item.type : null;
    const src = typeof item.src === 'string' ? item.src : '';
    if (!type || !src) continue;
    out.push({
      type,
      src,
      title: typeof item.title === 'string' ? item.title : undefined,
    });
  }
  return out;
}

function parseCarouselSlide(item: unknown): I3EventCarouselSlide | null {
  if (!isRecord(item)) return null;
  const type = item.type === 'image' || item.type === 'video' ? item.type : null;
  const src = typeof item.src === 'string' ? item.src.trim() : '';
  if (!type || !src) return null;
  return {
    type,
    src,
    poster: typeof item.poster === 'string' && item.poster.trim() ? item.poster.trim() : undefined,
    caption: typeof item.caption === 'string' ? item.caption : undefined,
    title: typeof item.title === 'string' ? item.title : undefined,
  };
}

function parseLatestHighlight(raw: unknown, fallback: I3LatestEventHighlight): I3LatestEventHighlight {
  if (!isRecord(raw)) return fallback;
  const id = typeof raw.id === 'string' && raw.id.trim() ? raw.id.trim() : fallback.id;
  const title = typeof raw.title === 'string' && raw.title.trim() ? raw.title.trim() : fallback.title;
  const descriptionHtml =
    typeof raw.descriptionHtml === 'string' ? raw.descriptionHtml : fallback.descriptionHtml;
  const slidesRaw = raw.carouselSlides;
  const carouselSlides = Array.isArray(slidesRaw)
    ? slidesRaw.map(parseCarouselSlide).filter((x): x is I3EventCarouselSlide => x !== null)
    : fallback.carouselSlides;
  const videos = Array.isArray(raw.videos)
    ? parseMedia(raw.videos).filter((m) => m.type === 'video').slice(0, 4)
    : fallback.videos;
  const photos = Array.isArray(raw.photos)
    ? parseMedia(raw.photos).filter((m) => m.type === 'image')
    : fallback.photos;
  return {
    ...fallback,
    id,
    title,
    label: typeof raw.label === 'string' ? raw.label : fallback.label,
    dateLine: typeof raw.dateLine === 'string' ? raw.dateLine : fallback.dateLine,
    descriptionHtml,
    blogPostSlug:
      typeof raw.blogPostSlug === 'string' && raw.blogPostSlug.trim()
        ? raw.blogPostSlug.trim()
        : undefined,
    carouselSlides,
    videos,
    photos,
  };
}

function parseUpcomingEvent(item: unknown): I3UpcomingEvent | null {
  if (!isRecord(item)) return null;
  const id = typeof item.id === 'string' ? item.id : '';
  const title = typeof item.title === 'string' ? item.title : '';
  if (!id || !title) return null;
  return {
    id,
    title,
    date: typeof item.date === 'string' ? item.date : undefined,
    time: typeof item.time === 'string' ? item.time : undefined,
    checkIn: typeof item.checkIn === 'string' ? item.checkIn : undefined,
    location: typeof item.location === 'string' ? item.location : undefined,
    descriptionHtml: typeof item.descriptionHtml === 'string' ? item.descriptionHtml : undefined,
    registerUrl:
      typeof item.registerUrl === 'string' && item.registerUrl.trim()
        ? item.registerUrl.trim()
        : undefined,
    ctaLabel:
      typeof item.ctaLabel === 'string' && item.ctaLabel.trim() ? item.ctaLabel.trim() : undefined,
  };
}

function parseUpcomingEventsList(raw: unknown): I3UpcomingEvent[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(parseUpcomingEvent).filter((x): x is I3UpcomingEvent => x !== null);
}

function parseHomeSpotlight(raw: unknown): I3HomeEventSpotlight | undefined {
  if (!isRecord(raw)) return undefined;
  const image = typeof raw.image === 'string' && raw.image.trim() ? raw.image.trim() : '';
  if (!image) return undefined;
  return {
    image,
    headline: typeof raw.headline === 'string' ? raw.headline : undefined,
    subline: typeof raw.subline === 'string' ? raw.subline : undefined,
    linkLabel: typeof raw.linkLabel === 'string' ? raw.linkLabel : undefined,
    linkTo: typeof raw.linkTo === 'string' && raw.linkTo.trim() ? raw.linkTo.trim() : undefined,
  };
}

function parseSaveTheDate(raw: unknown): I3SaveTheDateAnnouncement | undefined {
  if (!isRecord(raw)) return undefined;
  const id = typeof raw.id === 'string' && raw.id.trim() ? raw.id.trim() : '';
  const title = typeof raw.title === 'string' && raw.title.trim() ? raw.title.trim() : '';
  const date = typeof raw.date === 'string' && raw.date.trim() ? raw.date.trim() : '';
  if (!id || !title || !date) return undefined;
  return {
    id,
    enabled: raw.enabled !== false,
    eyebrow: typeof raw.eyebrow === 'string' && raw.eyebrow.trim() ? raw.eyebrow.trim() : undefined,
    title,
    subtitle: typeof raw.subtitle === 'string' && raw.subtitle.trim() ? raw.subtitle.trim() : undefined,
    date,
    time: typeof raw.time === 'string' && raw.time.trim() ? raw.time.trim() : undefined,
    body: typeof raw.body === 'string' && raw.body.trim() ? raw.body.trim() : undefined,
    primaryCtaLabel:
      typeof raw.primaryCtaLabel === 'string' && raw.primaryCtaLabel.trim()
        ? raw.primaryCtaLabel.trim()
        : undefined,
    primaryCtaTo:
      typeof raw.primaryCtaTo === 'string' && raw.primaryCtaTo.trim()
        ? raw.primaryCtaTo.trim()
        : undefined,
    dismissLabel:
      typeof raw.dismissLabel === 'string' && raw.dismissLabel.trim()
        ? raw.dismissLabel.trim()
        : undefined,
  };
}

function parseEventsPage(raw: unknown): I3EventsPageCms {
  const d = DEFAULT_SITE_CONTENT.eventsPage;
  if (!isRecord(raw)) return d;
  const lh =
    raw.latestHighlight !== undefined && raw.latestHighlight !== null
      ? parseLatestHighlight(raw.latestHighlight, d.latestHighlight)
      : d.latestHighlight;
  let homeSpotlight: I3EventsPageCms['homeSpotlight'] = d.homeSpotlight;
  if (raw.homeSpotlight === null) {
    homeSpotlight = undefined;
  } else if (isRecord(raw.homeSpotlight)) {
    const parsed = parseHomeSpotlight(raw.homeSpotlight);
    if (parsed) homeSpotlight = parsed;
  }
  return {
    pageSubtitle: typeof raw.pageSubtitle === 'string' ? raw.pageSubtitle : d.pageSubtitle,
    latestHighlight: lh,
    upcomingSectionTitle:
      typeof raw.upcomingSectionTitle === 'string' ? raw.upcomingSectionTitle : d.upcomingSectionTitle,
    upcomingEvents: Array.isArray(raw.upcomingEvents)
      ? parseUpcomingEventsList(raw.upcomingEvents)
      : d.upcomingEvents,
    upcomingEmptyHtml:
      typeof raw.upcomingEmptyHtml === 'string' ? raw.upcomingEmptyHtml : d.upcomingEmptyHtml,
    listingWorkflowNoteHtml:
      typeof raw.listingWorkflowNoteHtml === 'string'
        ? raw.listingWorkflowNoteHtml
        : d.listingWorkflowNoteHtml,
    homeSpotlight,
  };
}

function parsePastEvents(raw: unknown): I3PastEvent[] {
  if (!Array.isArray(raw)) return DEFAULT_SITE_CONTENT.pastEvents;
  const out: I3PastEvent[] = [];
  for (const item of raw) {
    if (!isRecord(item)) continue;
    const id = typeof item.id === 'string' ? item.id : '';
    const title = typeof item.title === 'string' ? item.title : '';
    const description = typeof item.description === 'string' ? item.description : '';
    if (!id || !title) continue;
    out.push({
      id,
      title,
      description,
      date: typeof item.date === 'string' ? item.date : undefined,
      media: parseMedia(item.media),
    });
  }
  return out.length > 0 ? out : DEFAULT_SITE_CONTENT.pastEvents;
}

function parseFeatured(raw: unknown): I3FeaturedEvent {
  if (!isRecord(raw)) return DEFAULT_SITE_CONTENT.featuredEvent;
  const pay =
    typeof raw.payCloverUrl === 'string' && raw.payCloverUrl.trim() !== ''
      ? raw.payCloverUrl
      : i3Assets.cloverPaymentUrl;
  const fe = DEFAULT_SITE_CONTENT.featuredEvent;
  return {
    id: typeof raw.id === 'string' ? raw.id : fe.id,
    label: typeof raw.label === 'string' ? raw.label : fe.label,
    title: typeof raw.title === 'string' ? raw.title : fe.title,
    year: typeof raw.year === 'string' ? raw.year : fe.year,
    date: typeof raw.date === 'string' ? raw.date : fe.date,
    time: typeof raw.time === 'string' ? raw.time : fe.time,
    checkIn: typeof raw.checkIn === 'string' ? raw.checkIn : fe.checkIn,
    location: typeof raw.location === 'string' ? raw.location : fe.location,
    description: typeof raw.description === 'string' ? raw.description : fe.description,
    registerTypeformUrl:
      typeof raw.registerTypeformUrl === 'string' ? raw.registerTypeformUrl : fe.registerTypeformUrl,
    payCloverUrl: pay,
    heroImage: typeof raw.heroImage === 'string' && raw.heroImage.trim() !== '' ? raw.heroImage : undefined,
  };
}

function parseBlogPosts(raw: unknown): I3BlogPostCms[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: I3BlogPostCms[] = [];
  for (const item of raw) {
    if (!isRecord(item)) continue;
    const slug = typeof item.slug === 'string' ? item.slug : '';
    const title = typeof item.title === 'string' ? item.title : '';
    if (!slug || !title) continue;
    const statusRaw = item.status;
    const status =
      statusRaw === 'draft' || statusRaw === 'archived' || statusRaw === 'published'
        ? statusRaw
        : 'published';
    const paragraphs = Array.isArray(item.paragraphs)
      ? item.paragraphs.filter((p): p is string => typeof p === 'string')
      : [];
    const heroImage =
      typeof item.heroImage === 'string' && item.heroImage.trim() !== '' ? item.heroImage.trim() : undefined;
    const formatRaw = item.format;
    const format =
      formatRaw === 'daily' || formatRaw === 'devotional' || formatRaw === 'article'
        ? formatRaw
        : undefined;
    out.push({
      slug,
      title,
      excerpt: typeof item.excerpt === 'string' ? item.excerpt : '',
      author: typeof item.author === 'string' ? item.author : 'I-3 Coaching & Consulting',
      publishedAt: typeof item.publishedAt === 'string' ? item.publishedAt : '2026-01-01',
      format,
      topic: typeof item.topic === 'string' && item.topic.trim() !== '' ? item.topic.trim() : undefined,
      featured: typeof item.featured === 'boolean' ? item.featured : undefined,
      readTimeMinutes: typeof item.readTimeMinutes === 'number' ? item.readTimeMinutes : undefined,
      heroImage,
      quote: typeof item.quote === 'string' && item.quote.trim() !== '' ? item.quote.trim() : undefined,
      meaning: typeof item.meaning === 'string' && item.meaning.trim() !== '' ? item.meaning.trim() : undefined,
      paragraphs,
      status,
      scheduledPublishAt:
        typeof item.scheduledPublishAt === 'string' ? item.scheduledPublishAt : undefined,
    });
  }
  return out.length > 0 ? out : undefined;
}

function parseOperations(raw: unknown): I3OperationsSnapshot | undefined {
  if (!isRecord(raw)) return undefined;
  const headline = typeof raw.headline === 'string' ? raw.headline : undefined;
  const notes = typeof raw.notes === 'string' ? raw.notes : undefined;
  const featuredEventRegistrationCount =
    typeof raw.featuredEventRegistrationCount === 'number'
      ? raw.featuredEventRegistrationCount
      : undefined;
  const totalInquiriesCount =
    typeof raw.totalInquiriesCount === 'number' ? raw.totalInquiriesCount : undefined;
  if (!headline && featuredEventRegistrationCount === undefined && totalInquiriesCount === undefined && !notes) {
    return undefined;
  }
  return {
    headline,
    notes,
    featuredEventRegistrationCount,
    totalInquiriesCount,
  };
}

function parseServiceCard(item: unknown): I3ServiceCard | null {
  if (!isRecord(item)) return null;
  const id = typeof item.id === 'string' ? item.id : '';
  const title = typeof item.title === 'string' ? item.title : '';
  if (!id || !title) return null;
  const outcomesRaw = item.outcomes;
  const outcomes = Array.isArray(outcomesRaw)
    ? outcomesRaw.filter((x): x is string => typeof x === 'string')
    : [];
  return {
    id,
    title,
    whoItsFor: typeof item.whoItsFor === 'string' ? item.whoItsFor : '',
    summary: typeof item.summary === 'string' ? item.summary : '',
    outcomes,
    detailHtml: typeof item.detailHtml === 'string' ? item.detailHtml : '<p></p>',
    bookUrl: typeof item.bookUrl === 'string' ? item.bookUrl : '',
    payUrl: typeof item.payUrl === 'string' && item.payUrl.trim() ? item.payUrl : undefined,
  };
}

function parseServicesPillar(item: unknown): I3ServicesPillar | null {
  if (!isRecord(item)) return null;
  const id = typeof item.id === 'string' ? item.id : '';
  const title = typeof item.title === 'string' ? item.title : '';
  if (!id || !title) return null;
  const cardsRaw = item.cards;
  const cards = Array.isArray(cardsRaw)
    ? cardsRaw.map(parseServiceCard).filter((c): c is I3ServiceCard => c !== null)
    : [];
  const hidePillarHeading =
    typeof item.hidePillarHeading === 'boolean' ? item.hidePillarHeading : false;
  return {
    id,
    title,
    subtitle: typeof item.subtitle === 'string' ? item.subtitle : undefined,
    hidePillarHeading,
    cards,
  };
}

function parseServicesCms(raw: unknown): I3ServicesCms {
  if (!isRecord(raw)) return DEFAULT_SITE_CONTENT.services;
  if (Array.isArray(raw.pillars)) {
    const pillars = raw.pillars
      .map(parseServicesPillar)
      .filter((p): p is I3ServicesPillar => p !== null)
      .filter((p) => p.cards.length > 0);
    if (pillars.length > 0) {
      return {
        pageSubtitle: typeof raw.pageSubtitle === 'string' ? raw.pageSubtitle : undefined,
        pillars,
      };
    }
  }
  return DEFAULT_SITE_CONTENT.services;
}

export function parseSiteContentPayload(raw: unknown): I3SiteContentV1 {
  if (!isRecord(raw)) return DEFAULT_SITE_CONTENT;
  if (raw.version !== '1' && raw.version !== 1) return DEFAULT_SITE_CONTENT;

  const moreEventsCopy =
    typeof raw.moreEventsCopy === 'string' ? raw.moreEventsCopy : DEFAULT_SITE_CONTENT.moreEventsCopy;

  const pastEventsIntro =
    typeof raw.pastEventsIntro === 'string' ? raw.pastEventsIntro : DEFAULT_SITE_CONTENT.pastEventsIntro;

  const blogPosts = parseBlogPosts(raw.blogPosts);
  const operations = parseOperations(raw.operations);

  const services = parseServicesCms(raw.services);
  const eventsPage = parseEventsPage(raw.eventsPage);
  const saveTheDate =
    raw.saveTheDate === null
      ? undefined
      : parseSaveTheDate(raw.saveTheDate) ?? DEFAULT_SITE_CONTENT.saveTheDate;

  return {
    version: '1',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : undefined,
    featuredEvent: parseFeatured(raw.featuredEvent),
    eventsPage,
    saveTheDate,
    pastEvents: parsePastEvents(raw.pastEvents),
    moreEventsCopy,
    pastEventsIntro,
    services,
    blogPosts,
    operations,
  };
}
